import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Heart, Users, MessageSquare, Lock, Mail, AlertCircle } from "lucide-react";

// Bump this version to force ALL users to re-accept (e.g., after policy changes)
const LEGAL_VERSION = "v1.2026.05";
const STORAGE_KEY = "canales_legal_accepted";

export function hasAcceptedLegal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed.version === LEGAL_VERSION;
  } catch {
    return false;
  }
}

const CONTENT = {
  es: {
    eyebrow: "AVISO LEGAL · ANTES DE CONTINUAR",
    title: "Bienvenida a la plataforma interna de Mueblería Canales.",
    intro: "Antes de iniciar sesión lee los puntos siguientes. Son las reglas y responsabilidades básicas para usar esta plataforma de capacitación.",
    points: [
      {
        icon: Heart,
        h: "La plataforma es gratuita y de uso interno.",
        p: "No tiene ningún costo para el equipo de Mueblería Canales. Es una herramienta propiedad de la empresa, creada para apoyar tu formación. No la distribuyas fuera de la empresa ni le des acceso a personas ajenas.",
      },
      {
        icon: Users,
        h: "Solo accede personal autorizado.",
        p: "Para tener cuenta necesitas autorización de un administrador o manager. Tu cuenta es personal e intransferible — no compartas tu contraseña, ni tu correo corporativo, ni prestes tu dispositivo con la sesión abierta.",
      },
      {
        icon: ShieldCheck,
        h: "Responsabilidades de uso.",
        p: "Úsala solo para capacitación. No intentes ver cuentas de otros, no descargues materiales para redistribuir, no subas contenido ilegal u ofensivo. Si detectas una brecha, repórtalo de inmediato al administrador.",
      },
      {
        icon: MessageSquare,
        h: "Notificaciones automáticas.",
        p: "Al iniciar sesión, completar cursos o alcanzar hitos de puntos se dispara un SMS al equipo de dirección (3 números autorizados). También se envían correos al asignarte cursos o exámenes, y a tu manager cuando termines evaluaciones. Esto es parte del monitoreo operativo acordado.",
      },
      {
        icon: Lock,
        h: "Privacidad de tus datos.",
        p: "Guardamos datos mínimos: nombre, correo corporativo, progreso de capacitación, calificaciones y actividad en la plataforma. NO vendemos datos, NO hacemos publicidad. Detalles completos en el Aviso de Privacidad.",
      },
      {
        icon: AlertCircle,
        h: "Suspensión y soporte.",
        p: "La empresa puede suspender tu acceso si incumples estas reglas o si finaliza tu relación laboral. Para cualquier pregunta, soporte o para ejercer tus derechos sobre tus datos, escribe a antonio@canalesfranchise.com.",
      },
    ],
    agree_privacy: "He leído y acepto el",
    agree_privacy_link: "Aviso de Privacidad",
    agree_privacy_suffix: "de Canales Franchise LLC.",
    agree_terms: "He leído y acepto los",
    agree_terms_link: "Términos y Condiciones",
    agree_terms_suffix: "de la plataforma.",
    continue: "Aceptar y continuar al inicio de sesión",
    continue_disabled: "Marca ambas casillas para continuar",
    read_docs: "Leer los documentos completos:",
    lang_es: "ES",
    lang_en: "EN",
    version_label: "Versión",
  },
  en: {
    eyebrow: "LEGAL NOTICE · BEFORE YOU CONTINUE",
    title: "Welcome to Muebleria Canales' internal platform.",
    intro: "Before logging in, please read the points below. These are the basic rules and responsibilities for using this training platform.",
    points: [
      {
        icon: Heart,
        h: "The platform is free and for internal use.",
        p: "There is no cost for Muebleria Canales staff. It is a company-owned tool to support your training. Do not distribute it outside the company or grant access to outsiders.",
      },
      {
        icon: Users,
        h: "Authorized personnel only.",
        p: "You need authorization from an administrator or manager to have an account. Your account is personal and non-transferable — do not share your password, corporate email, or leave your device unattended with the session open.",
      },
      {
        icon: ShieldCheck,
        h: "Usage responsibilities.",
        p: "Use it only for training. Do not attempt to access other accounts, do not download materials for redistribution, do not upload illegal or offensive content. If you detect a breach, report it immediately to the administrator.",
      },
      {
        icon: MessageSquare,
        h: "Automatic notifications.",
        p: "When you log in, complete courses, or reach point milestones, an SMS is sent to the leadership team (3 authorized numbers). Emails are also sent when you are assigned courses/exams, and to your manager when you finish evaluations. This is part of agreed operational monitoring.",
      },
      {
        icon: Lock,
        h: "Data privacy.",
        p: "We store minimal data: name, corporate email, training progress, scores, and platform activity. We do NOT sell data, we do NOT run advertising. Full details in the Privacy Notice.",
      },
      {
        icon: AlertCircle,
        h: "Suspension and support.",
        p: "The company may suspend access if you breach these rules or if your employment ends. For any question, support, or to exercise your rights over your data, email antonio@canalesfranchise.com.",
      },
    ],
    agree_privacy: "I have read and accept the",
    agree_privacy_link: "Privacy Policy",
    agree_privacy_suffix: "of Canales Franchise LLC.",
    agree_terms: "I have read and accept the",
    agree_terms_link: "Terms & Conditions",
    agree_terms_suffix: "of the platform.",
    continue: "Accept and continue to login",
    continue_disabled: "Check both boxes to continue",
    read_docs: "Read the full documents:",
    lang_es: "ES",
    lang_en: "EN",
    version_label: "Version",
  },
};

export default function LegalGate({ onAccept }) {
  // English-only mode: locale is fixed to "en" and the toggle is hidden.
  // To re-enable the bilingual toggle, restore the original useState below
  // and remove the `false &&` guard around the toggle markup further down.
  const [locale, setLocale] = useState("en");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const c = CONTENT[locale];

  // Prevent body scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const canContinue = agreePrivacy && agreeTerms;

  const accept = () => {
    if (!canContinue) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: LEGAL_VERSION,
        accepted_at: new Date().toISOString(),
        locale,
      }));
    } catch {
      /* storage blocked - still allow */
    }
    onAccept?.({ version: LEGAL_VERSION, locale });
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="legal-gate-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-gate-title"
    >
      <div className="bg-[#FFFFFF] rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden border border-[#E5E7EB]">
        {/* Header */}
        <div className="bg-[#1B2E7A] text-white px-6 sm:px-10 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#F4B81F] font-medium">
              {c.eyebrow}
            </p>
            <h1 id="legal-gate-title" className="font-heading text-2xl sm:text-3xl mt-1 leading-tight">
              {c.title}
            </h1>
          </div>
          {/* Language toggle hidden — app is in English-only mode */}
          {false && (
            <div className="hidden sm:flex items-center gap-1.5 text-[10px]">
              <button
                onClick={() => setLocale("es")}
                className={`px-2.5 py-1 rounded uppercase tracking-[0.16em] ${locale === "es" ? "bg-white text-[#1B2E7A] font-medium" : "text-white/70 hover:text-white"}`}
                data-testid="legal-lang-es"
              >{c.lang_es}</button>
              <button
                onClick={() => setLocale("en")}
                className={`px-2.5 py-1 rounded uppercase tracking-[0.16em] ${locale === "en" ? "bg-white text-[#1B2E7A] font-medium" : "text-white/70 hover:text-white"}`}
                data-testid="legal-lang-en"
              >{c.lang_en}</button>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 sm:px-10 py-6 flex-1">
          <p className="text-sm text-[#374151] leading-relaxed mb-6">{c.intro}</p>

          <div className="space-y-5 mb-8">
            {c.points.map(({ icon: Icon, h, p }, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`legal-point-${i}`}>
                <div className="h-9 w-9 rounded-md bg-[#1B2E7A]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="h-4 w-4 text-[#1B2E7A]" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base text-[#1A1A1A] mb-1 leading-snug">{h}</p>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{p}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Docs links */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 mb-6">
            <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> {c.read_docs}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#1B2E7A] hover:text-[#13205A] underline underline-offset-2 inline-flex items-center gap-1"
                data-testid="legal-open-privacy"
              >
                → {c.agree_privacy_link}
              </Link>
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#1B2E7A] hover:text-[#13205A] underline underline-offset-2 inline-flex items-center gap-1"
                data-testid="legal-open-terms"
              >
                → {c.agree_terms_link}
              </Link>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="legal-agree-privacy-label">
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#1B2E7A]"
                data-testid="legal-agree-privacy"
              />
              <span className="text-sm text-[#1A1A1A] leading-relaxed">
                {c.agree_privacy}{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1B2E7A] underline underline-offset-2 hover:text-[#13205A]">
                  {c.agree_privacy_link}
                </Link>
                {" "}{c.agree_privacy_suffix}
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group" data-testid="legal-agree-terms-label">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#1B2E7A]"
                data-testid="legal-agree-terms"
              />
              <span className="text-sm text-[#1A1A1A] leading-relaxed">
                {c.agree_terms}{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1B2E7A] underline underline-offset-2 hover:text-[#13205A]">
                  {c.agree_terms_link}
                </Link>
                {" "}{c.agree_terms_suffix}
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5E7EB] bg-white px-6 sm:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-[0.2em]">
            {c.version_label} {LEGAL_VERSION}
          </p>
          <button
            onClick={accept}
            disabled={!canContinue}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
              canContinue
                ? "bg-[#1B2E7A] hover:bg-[#13205A] text-white shadow-md"
                : "bg-[#E5E7EB] text-[#6B7280] cursor-not-allowed"
            }`}
            data-testid="legal-continue-btn"
          >
            <CheckCircle2 className="h-4 w-4" />
            {canContinue ? c.continue : c.continue_disabled}
          </button>
        </div>
      </div>
    </div>
  );
}
